"use client";
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  Spinner,
} from "@fluentui/react-components";
import ASCButton from "../../../components/Button";

export interface TemplateItem {
  title: string;
  code: string;
  description: string;
}

interface TemplateSelectionModalProps {
  open: boolean;
  templates: TemplateItem[];
  isTemplatesLoading: boolean;
  selectedCode: string;
  onSelect: (title: string, code: string) => void;
  onClose: () => void;
}

const PAGE_SIZE = 8;

const PagButton: React.FC<{
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
  title?: string;
}> = ({ onClick, disabled, children, title }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      width: 26,
      height: 26,
      border: "1px solid #d1d9e6",
      borderRadius: 3,
      background: "#fff",
      cursor: disabled ? "not-allowed" : "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#5a6a85",
      opacity: disabled ? 0.35 : 1,
      padding: 0,
      flexShrink: 0,
    }}
  >
    {children}
  </button>
);

const thStyle: React.CSSProperties = {
  background: "#f5f7fb",
  padding: "7px 10px",
  textAlign: "left",
  fontSize: 11.5,
  fontWeight: 600,
  color: "#5a6a85",
  borderBottom: "1px solid #d1d9e6",
  whiteSpace: "nowrap",
};

const tdBase: React.CSSProperties = {
  padding: "7px 10px",
  borderBottom: "1px solid #e8ecf3",
  verticalAlign: "top",
};

const TemplateSelectionModal: React.FC<TemplateSelectionModalProps> = ({
  open,
  templates,
  isTemplatesLoading,
  selectedCode,
  onSelect,
  onClose,
}) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pendingCode, setPendingCode] = useState<string>(selectedCode);

  useEffect(() => {
    if (open) {
      setSearch("");
      setPage(1);
      setPendingCode(selectedCode);
    }
  }, [open, selectedCode]);

  const filtered = templates.filter((t) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      t.title.toLowerCase().includes(q) ||
      (t.description || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const sliceStart = (safePage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(sliceStart, sliceStart + PAGE_SIZE);
  const from = filtered.length === 0 ? 0 : sliceStart + 1;
  const to = Math.min(sliceStart + PAGE_SIZE, filtered.length);

  const pendingTemplate = templates.find((t) => t.code === pendingCode);

  const goPage = (dir: "first" | "prev" | "next" | "last") => {
    if (dir === "first") {
      setPage(1);
    } else if (dir === "prev") {
      setPage((p) => Math.max(1, p - 1));
    } else if (dir === "next") {
      setPage((p) => Math.min(totalPages, p + 1));
    } else if (dir === "last") {
      setPage(totalPages);
    }
  };

  // Force-correct the current page index back within valid boundaries whenever filters shrink the dataset
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [filtered.length, totalPages, page]);

  const handleConfirm = () => {
    if (pendingTemplate) {
      onSelect(pendingTemplate.title, pendingTemplate.code);
    }
  };

  return (
    <Dialog modalType="alert" open={open}>
      <DialogSurface style={{ maxWidth: 700, padding: 0, overflow: "hidden" }}>
        <DialogBody
          style={{
            padding: 0,
            gap: 0,
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          }}
        >
          {/* ── Header ── */}
          <div
            style={{
              padding: "12px 20px",
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #e8ecf3",
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              Template Selection
            </span>
          </div>

          {/* ── Body ── */}
          <DialogContent
            style={{ padding: "14px 20px", flex: 1, overflow: "auto" }}
          >
            {/* Search */}
            <div style={{ position: "relative", marginBottom: 10 }}>
              <svg
                width="13"
                height="13"
                viewBox="0 0 13 13"
                fill="none"
                style={{
                  position: "absolute",
                  left: 9,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9aa5b8",
                  pointerEvents: "none",
                }}
              >
                <circle
                  cx="5.5"
                  cy="5.5"
                  r="4.5"
                  stroke="currentColor"
                  strokeWidth="1.3"
                />
                <path
                  d="M9.5 9.5L12.5 12.5"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                placeholder="Search by title or description…"
                autoComplete="off"
                style={{
                  width: "100%",
                  height: 32,
                  padding: "0 10px 0 30px",
                  border: "1px solid #d1d9e6",
                  borderRadius: 5,
                  fontSize: 12.5,
                  fontFamily: "inherit",
                  outline: "none",
                  color: "#1a202c",
                  background: "#fff",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Table */}
            <div
              style={{
                border: "1px solid #e8ecf3",
                borderRadius: 5,
                overflow: "hidden",
              }}
            >
              <div style={{ maxHeight: 320, overflowY: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12.5,
                    tableLayout: "fixed",
                  }}
                >
                  <colgroup>
                    <col style={{ width: 40 }} />
                    <col style={{ width: "40%" }} />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={thStyle} />
                      <th style={thStyle}>Title</th>
                      <th style={thStyle}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      isTemplatesLoading ? (
                        <tr>
                          <td
                            colSpan={3}
                            style={{
                              padding: 14,
                              textAlign: "center",
                              fontSize: 12,
                              color: "#9aa5b8",
                            }}
                          >
                            <Spinner />
                          </td>
                        </tr>
                      ) :
                        pageItems.length === 0 ? (
                          <tr>
                            <td
                              colSpan={3}
                              style={{
                                padding: 14,
                                textAlign: "center",
                                fontSize: 12,
                                color: "#9aa5b8",
                              }}
                            >
                              No templates found.
                            </td>
                          </tr>
                        ) : (
                          pageItems.map((item) => {
                            const isSelected = item.code === pendingCode;
                            return (
                              <tr
                                key={item.code}
                                onClick={() => setPendingCode(item.code)}
                                style={{
                                  cursor: "pointer",
                                  background: isSelected ? "#edf2fb" : undefined,
                                }}
                              >
                                <td style={{ ...tdBase, textAlign: "center" }}>
                                  <input
                                    type="radio"
                                    name="tmplRadio"
                                    checked={isSelected}
                                    onChange={() => setPendingCode(item.code)}
                                    style={{
                                      accentColor: "#205393",
                                      cursor: "pointer",
                                    }}
                                  />
                                </td>
                                <td
                                  style={{
                                    ...tdBase,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.title}
                                </td>
                                <td
                                  style={{
                                    ...tdBase,
                                    fontSize: 12,
                                    color: "#5a6a85",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {item.description}
                                </td>
                              </tr>
                            );
                          })
                        )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 6,
                marginTop: 10,
                fontSize: 12,
                color: "#5a6a85",
              }}
            >
              <span>
                {filtered.length === 0
                  ? "0 of 0"
                  : `${from}–${to} of ${filtered.length}`}
              </span>
              <PagButton
                onClick={() => goPage("first")}
                disabled={safePage <= 1}
                title="First page"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M9 2L5 6l4 4M3 2v8"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </PagButton>
              <PagButton
                onClick={() => goPage("prev")}
                disabled={safePage <= 1}
                title="Previous page"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M7.5 2.5L4 6l3.5 3.5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </PagButton>
              <PagButton
                onClick={() => goPage("next")}
                disabled={safePage >= totalPages}
                title="Next page"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M4.5 2.5L8 6l-3.5 3.5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </PagButton>
              <PagButton
                onClick={() => goPage("last")}
                disabled={safePage >= totalPages}
                title="Last page"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M3 2l4 4-4 4M9 2v8"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </PagButton>
            </div>
          </DialogContent>

          {/* ── Footer ── */}
          <div
            style={{
              padding: "11px 20px",
              borderTop: "1px solid #e8ecf3",
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "#f8f9fc",
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 12, color: "#5a6a85", flex: 1 }}>
              {pendingTemplate
                ? `Selected: ${pendingTemplate.title}`
                : "No template selected"}
            </span>
            <ASCButton
              shape="rounded"
              appearance="secondary"
              onClick={onClose}
              label="Close"
            />
            <ASCButton
              shape="rounded"
              appearance="primary"
              className="asc-button-primary"
              onClick={handleConfirm}
              disabled={!pendingCode}
              label="Select"
            />
          </div>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default TemplateSelectionModal;
